import React, { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Either, pipe } from 'effect';

import { useResetTextOverride, useSaveTextOverride } from '@/domain/textOverrides/queries/bundle';
import {
  messageSignature,
  validateOverrideValue,
  type MessageSignature,
} from '@/lib/i18nOverrides/messageSignature';
import type { MarkerEntry } from '@/lib/i18nOverrides/registry';
import { EDITOR_UI_ATTRIBUTE } from '@/components/TextOverrides/editorUi';
import { useTextOverridesContext } from '@/components/TextOverrides/TextOverridesContext';

export interface DialogTarget {
  readonly entry: MarkerEntry;
  /** Text nodes + attributes on screen that carry this key, counted when the dialog opened. */
  readonly usedIn: number;
}

interface Props {
  readonly orgId: string;
  readonly targets: ReadonlyArray<DialogTarget>;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </Typography>
  </Box>
);

/** Edits one key for the org and the current locale. Rendered under the unmarked intl, so nothing here is pickable. */
const TextOverrideDialog = ({ orgId, targets, onClose, onSaved }: Props) => {
  const { baseMessages, overrides, messages, locale } = useTextOverridesContext();
  const [selectedId, setSelectedId] = useState(targets[0].entry.id);
  const target = targets.find((t) => t.entry.id === selectedId) ?? targets[0];
  const { id, defaultMessage } = target.entry;

  // The original is the static catalog text of the locale, or the code's default text when the catalog lacks the key.
  const original = baseMessages[id] ?? defaultMessage ?? '';
  const hasOverride = id in overrides;
  const current = messages[id] ?? overrides[id] ?? original;

  const [values, setValues] = useState<Readonly<Record<string, string>>>({});
  const value = values[id] ?? current;

  const save = useSaveTextOverride(orgId, locale);
  const reset = useResetTextOverride(orgId, locale);

  const reasons = useMemo(() => validateOverrideValue(value, original), [value, original]);
  // Each placeholder/tag of the original, and whether the new text still has it.
  const required = useMemo(() => {
    const present = pipe(
      messageSignature(value),
      Either.getOrElse((): MessageSignature => ({ args: [], tags: [] })),
    );
    return pipe(
      messageSignature(original),
      Either.match({
        onLeft: () => [],
        onRight: (s) => [
          ...s.args.map((a) => ({ label: `{${a}}`, ok: present.args.includes(a) })),
          ...s.tags.map((t) => ({ label: `<${t}>`, ok: present.tags.includes(t) })),
        ],
      }),
    );
  }, [original, value]);

  const busy = save.isLoading || reset.isLoading;
  const serverError = save.error ?? reset.error;
  const canSave = reasons.length === 0 && value !== current && !busy;

  const onSave = () =>
    save.mutate(
      { key: id, value, baseValue: original },
      { onSuccess: () => onSaved(`Saved "${id}" for everyone in the organization`) },
    );
  const onReset = () => reset.mutate(id, { onSuccess: () => onSaved(`"${id}" is back to the original text`) });

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 'textOverridesEditor' }}
      {...{ [EDITOR_UI_ATTRIBUTE]: '' }}
    >
      <DialogTitle>Customize text</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {targets.length > 1 && (
            <TextField
              select
              size="small"
              label={`${targets.length} texts here — choose one`}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              slotProps={{ select: { MenuProps: { sx: { zIndex: 'textOverridesEditor' } } } }}
            >
              {targets.map((t) => (
                <MenuItem key={t.entry.id} value={t.entry.id}>
                  {t.entry.id}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Section label="Key">
            <Box component="code" sx={{ fontFamily: 'monospace' }}>
              {id}
            </Box>
          </Section>
          <Section label="Original text">{original || '—'}</Section>
          <Section label="Current text">
            {current || '—'}
            {hasOverride && <Chip size="small" label="customized" color="warning" sx={{ ml: 1 }} />}
          </Section>
          <Typography variant="body2" color="text.secondary">
            Used in {target.usedIn} {target.usedIn === 1 ? 'place' : 'places'} on screen. Changing it changes every
            place that uses this text, for everyone in the organization ({locale}).
          </Typography>
          <TextField
            label="New text"
            value={value}
            onChange={(e) => setValues((v) => ({ ...v, [id]: e.target.value }))}
            multiline
            minRows={2}
            autoFocus
            error={reasons.length > 0}
            helperText={reasons.length > 0 ? reasons.join(' ') : 'Keep the placeholders exactly as they are.'}
          />
          {required.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary">
                Required placeholders:
              </Typography>
              {required.map((p) => (
                <Chip key={p.label} size="small" variant="outlined" label={p.label} color={p.ok ? 'success' : 'error'} />
              ))}
            </Stack>
          )}
          {serverError && (
            <Alert severity="error">
              {serverError.message}
              {serverError.reasons.length > 0 && `: ${serverError.reasons.join('; ')}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReset} disabled={!hasOverride || busy} color="warning">
          Reset to original
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!canSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextOverrideDialog;
